/* eslint-disable max-len */
import React from 'react';
import { Checkbox } from '@makemydeal/ui-bricks/dist/cox';
import styled from 'styled-components';
import { UserConsent } from '@makemydeal/dr-platform-types';

const ShopperConsentCheckboxContainer = styled.div`
    display: inline;
    .checkbox-container {
        display: inline;
    }
    .checkbox-container > label {
        margin-bottom: 0;
    }
    .checkbox-container > label > div > div {
        width: 16px;
        height: 16px;
        margin-right: 8px;
    }
`;

const getSingleConsentText = ({ shopperTCPAProvider, dealerName }) => {
    switch (shopperTCPAProvider) {
        case 'amsi': {
            return `By executing this Agreement, you agree that we and third parties on our behalf may deliver/cause to be delivered telephonic sales calls 
            (including artificial or prerecorded voice messages, texts, or voicemails for marketing or any other lawful
                purposes) to you using an automated system for the selection or dialing of telephone numbers or the playing of a 
                recorded message to the telephone number(s) identified in this Agreement or provided to us, 
                even if the contact results in a charge. Consent is not required to purchase goods or services.`;
        }

        case 'bmw':
        case 'mini': {
            return `By checking here, I direct ${dealerName} to send me text messages to market or advertise products, goods, or services. These text messages may be transmitted through autodialed calls or robotext. By checking the checkbox and clicking submit, I confirm that I am the current owner/subscriber of the mobile number provided or that the current owner/subscribed of this mobile phone number authorized me to provide this number to the dealer. I understand that my consent is not required as a condition of purchase and that I can revoke my consent at any time. My carrier wireless and text message fees may apply. I will contact the dealer directly to provide reasonable notice if I no longer wish to receive automated calls or texts`;
        }

        default: {
            return `By checking the box, you consent to ${dealerName} and its authorized dealers using automatic telephone dialing systems to call and/or send you periodic SMS (text) marketing messages to the number you have provided. You do not have to consent in order to obtain any of our products or services. Message and data rates may apply.`;
        }
    }
};

const getDoubleConsentText: any = (shopperTCPAProvider: string): any => {
    switch (shopperTCPAProvider) {
        case 'genesis': {
            return {
                shopperConsentText1:
                    'By submitting your information, you agree to the sharing of your information between Genesis Motor America and its authorized retailers.',
                shopperConsentText2:
                    'I understand I do not have to consent as a condition of purchase or to receive any services. By checking this box, I agree Genesis, Genesis retailers and/or their vendors may use the number provided to make telemarketing calls or texts via automated technology. Carrier charges may apply.'
            };
        }

        case 'hyundai': {
            return {
                shopperConsentText1:
                    'By submitting your information, you agree to the sharing of your information between Hyundai Motor America and its authorized dealers.',
                shopperConsentText2:
                    'I understand I do not have to consent as a condition of purchase or to receive any services. By checking this box, I agree Hyundai, Hyundai dealers and/or their vendors may use the number provided to make telemarketing calls or texts via automated technology. Carrier charges may apply.'
            };
        }
    }
};

const renderSingleConsent = ({
    shopperTCPAProvider,
    dealerName,
    showTCPACheckbox,
    handleCheckbox,
    shopperInfoUserConsent,
    showShopperConsentText,
    creditAppDisclaimer
}) => {
    const shopperConsentText = getSingleConsentText({ shopperTCPAProvider, dealerName });

    return (
        <>
            {showTCPACheckbox && (
                <Checkbox
                    onChange={handleCheckbox}
                    checked={shopperInfoUserConsent === 'checked'}
                    data-testid="shopper-consent-checkbox"
                />
            )}
            {!showShopperConsentText && creditAppDisclaimer}
            {showShopperConsentText && (
                <>
                    <span data-testid="shopper-consent-text" className="shopper-consent-text">
                        <div>{shopperConsentText}</div>
                    </span>
                    {creditAppDisclaimer}
                </>
            )}
        </>
    );
};

const renderDoubleConsent = ({
    shopperTCPAProvider,
    showShopperConsentText,
    showTCPACheckbox,
    handleCheckbox,
    shopperInfoUserConsent,
    creditAppDisclaimer
}) => {
    const { shopperConsentText1, shopperConsentText2 } = getDoubleConsentText(shopperTCPAProvider);
    return (
        <>
            {showShopperConsentText && (
                <span data-testid="shopper-consent-text" className="shopper-consent-text">
                    {shopperConsentText1}
                </span>
            )}
            <div style={{ marginTop: '8px' }}>
                {showTCPACheckbox && (
                    <ShopperConsentCheckboxContainer>
                        <Checkbox
                            onChange={handleCheckbox}
                            checked={shopperInfoUserConsent === 'checked'}
                            data-testid="shopper-consent-checkbox"
                        />
                    </ShopperConsentCheckboxContainer>
                )}
                {!showShopperConsentText && creditAppDisclaimer}
                {showShopperConsentText && (
                    <>
                        <span data-testid="shopper-consent-text" className="shopper-consent-text">
                            {shopperConsentText2}
                        </span>
                        {creditAppDisclaimer}
                    </>
                )}
            </div>
        </>
    );
};

interface SelectedTemplateProps {
    dealerName: string;
    shopperTCPAProvider: string;
    shopperInfoUserConsent: UserConsent;
    handleCheckbox: (event: React.ChangeEvent<HTMLInputElement>) => void;
    showTCPACheckbox: boolean;
    creditAppDisclaimer: JSX.Element | null;
    showShopperConsentText: boolean;
}

export const renderSelectedTemplate = ({
    dealerName,
    shopperTCPAProvider,
    shopperInfoUserConsent,
    handleCheckbox,
    showTCPACheckbox,
    creditAppDisclaimer,
    showShopperConsentText
}: SelectedTemplateProps): JSX.Element => {
    if (shopperTCPAProvider === 'hyundai' || shopperTCPAProvider === 'genesis') {
        return renderDoubleConsent({
            shopperTCPAProvider,
            showShopperConsentText,
            showTCPACheckbox,
            handleCheckbox,
            shopperInfoUserConsent,
            creditAppDisclaimer
        });
    } else {
        return renderSingleConsent({
            shopperTCPAProvider,
            dealerName,
            showTCPACheckbox,
            handleCheckbox,
            shopperInfoUserConsent,
            showShopperConsentText,
            creditAppDisclaimer
        });
    }
};
